import { findIndex, first, isNil, last, uniq } from 'lodash';

import { fetchSubcampaign } from 'modules/subcampaigns/reducers/subcampaign';
import { fetchCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import { setConversationLastMessage } from 'modules/messenger/reducers/conversations';
import { markConversationAsRead } from 'modules/messenger/reducers/messengerNotifications';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import {
  compareAndMergeMeta,
  getCustomMetadataByState,
  getMessagesGroupingTitle,
  groupMessagesByDate
} from 'utils/messages';
import { t } from 'utils/language';
import { logError } from 'utils/log';
import { UserException } from 'utils/file';
import { generateTempUniqueId } from 'utils/id';
import { API, destroy, get, post } from 'utils/api';
import {
  ORGANIZATION_PROFILE_TYPE_BRAND,
  getOrganizationProfileIfExist,
  getOrganizationProfileType
} from 'utils/organization';
import { uploadFile } from 'utils/upload-asset';
import { getFileSizeInUnits } from 'utils/helper/common';

import type {
  MessengerBulkAttachment,
  MessengerBulkMessage,
  MessengerConversationMessagesResponse,
  MessengerMessage,
  MessengerMessagesMeta,
  MessengerReduxState,
  MessengerSendMessage,
  OrganizationProfileType,
  State,
  Subcampaign,
  ThunkAction
} from 'types';

const SENDING_MESSAGE = 'messages/SENDING_MESSAGE'; // will help us to prevent race conditions on load new messages
const LOADING = 'messages/LOADING';
const LOADING_MORE = 'messages/LOADING_MORE';
const UPDATE_PAGINATION = 'messages/UPDATE_PAGINATION';
const RECEIVE = 'messages/RECEIVE';
const RECEIVE_NEW = 'messages/RECEIVE_NEW';
const RECEIVE_PREVIOUS = 'messages/RECEIVE_PREVIOUS';
const RESET = 'messages/RESET';
const RECEIVE_NEW_MESSAGE = 'messages/RECEIVE_NEW_MESSAGE';
const ATTACHMENT_SUCCESS = 'messages/ATTACHMENT_SUCCESS';
const ATTACHMENT_FAILED = 'messages/ATTACHMENT_FAILED';
const UPDATE_MESSAGES_MAP = 'messages/UPDATE_MESSAGES_MAP';
const UPDATE_LAST_SEEN_MESSAGE = 'messages/UPDATE_LAST_SEEN_MESSAGE';
const MARK_MESSAGE_AS_DELETED = 'messages/MARK_MESSAGE_AS_DELETED';
const SET_META = 'messages/SET_META';

const ITEMS = 20;
const { MESSENGER } = API.END_POINTS;

const initialState: MessengerReduxState = {
  messagesBySubcampaign: {},
  metaBySubcampaign: {},
  lastSeenMessageBySubcampaign: {},
  isLoadingBySubcampaign: {},
  newMessagesMap: {},
  pagination: {
    page: 1,
    firstMessage: null,
    oldestMessage: null
  },
  conversationId: '',
  isSendingMessage: false,
  isLoadingMore: false
};

function setLoadingMore(isLoadingMore: boolean) {
  return {
    type: LOADING_MORE,
    payload: {
      isLoadingMore
    }
  };
}

function setLoading(isLoading: boolean, subcampaignId: number) {
  return {
    type: LOADING,
    payload: {
      isLoading,
      subcampaignId
    }
  };
}

function setMeta(meta: MessengerMessagesMeta, subcampaignId: number) {
  return {
    type: SET_META,
    payload: {
      meta,
      subcampaignId
    }
  };
}

function receivePreviousMessages(
  messages: Array<MessengerMessage>,
  meta: MessengerMessagesMeta,
  subcampaignId: number
) {
  return {
    type: RECEIVE_PREVIOUS,
    payload: {
      messages,
      meta,
      subcampaignId
    }
  };
}

function receiveNewMessages(
  messages: Array<MessengerMessage>,
  meta: MessengerMessagesMeta,
  subcampaignId: number
) {
  return {
    type: RECEIVE_NEW,
    payload: {
      messages,
      meta,
      subcampaignId
    }
  };
}

function saveNewMessagesMap(newMessagesMap: Record<string, boolean>) {
  return {
    type: UPDATE_MESSAGES_MAP,
    payload: {
      newMessagesMap
    }
  };
}

function receiveNewMessage(message: MessengerMessage, meta: MessengerMessagesMeta, subcampaignId: number) {
  return {
    type: RECEIVE_NEW_MESSAGE,
    payload: {
      message,
      meta,
      subcampaignId
    }
  };
}

function receiveMessages(
  messages: Array<MessengerMessage>,
  meta: MessengerMessagesMeta,
  subcampaignId: number,
  conversationId: string
) {
  return {
    type: RECEIVE,
    payload: {
      messages,
      meta,
      subcampaignId,
      conversationId
    }
  };
}

function updateLastSeenMessage(lastSeenMessage: MessengerMessage, subcampaignId: number) {
  return {
    type: UPDATE_LAST_SEEN_MESSAGE,
    payload: {
      lastSeenMessage,
      subcampaignId
    }
  };
}

function setPagination(page: number, firstMessage?: string | null, oldestMessage?: string | null) {
  return {
    type: UPDATE_PAGINATION,
    payload: {
      page,
      firstMessage,
      oldestMessage
    }
  };
}

function setSendingMessageStatus(isSending: boolean) {
  return {
    type: SENDING_MESSAGE,
    payload: {
      isSending
    }
  };
}

export function reset() {
  return {
    type: RESET
  };
}

export function setAttachmentSuccess(message: MessengerMessage, subcampaignId: number) {
  return {
    type: ATTACHMENT_SUCCESS,
    payload: {
      message,
      subcampaignId
    }
  };
}

function setAttachmentFailed(message: MessengerMessage, subcampaignId: number) {
  return {
    type: ATTACHMENT_FAILED,
    payload: {
      message,
      subcampaignId
    }
  };
}

function markMessageAsDeleted(deletedMessages: Array<MessengerMessage>, subcampaignId: number) {
  return {
    type: MARK_MESSAGE_AS_DELETED,
    payload: {
      deletedMessages,
      subcampaignId
    }
  };
}

function getTemporalUploadingMessage(message: MessengerSendMessage, state: State): MessengerMessage {
  const { currentAccount } = state.account;
  const { currentOrganization } = state.organizations;
  const uniqueId = generateTempUniqueId();
  const organizationProfile = getOrganizationProfileIfExist(currentOrganization);

  return {
    externalId: uniqueId,
    attachments: [{
      mimeType: message.file?.type,
      status: 'uploading'
    }],
    type: 'user',
    text: message.file?.name,
    senderId: currentAccount?.uuid ?? '',
    metadata: {
      profile: {
        id: organizationProfile && organizationProfile.id,
        type: getOrganizationProfileType(currentOrganization)
      }
    },
    isDeleted: false,
    createdAt: new Date().toISOString()
  };
}

function generateMessagesMap(
  newMessages: Array<MessengerMessage>,
  existingMessagesMap: Record<string, boolean>
): Record<string, boolean> {
  const cloneExistingMessagesMap = { ...existingMessagesMap };

  newMessages.forEach((message) => {
    cloneExistingMessagesMap[message.externalId] = true;
  });

  return cloneExistingMessagesMap;
}

function filterExistingMessagesByMap(
  newMessages: Array<MessengerMessage>,
  existingMessagesMap: Record<string, boolean>
): Array<MessengerMessage> {
  return newMessages.filter(newMessage => (
    isNil(existingMessagesMap[newMessage.externalId])
  ));
}

function getMetaBySubcampaignId(meta: Record<number, MessengerMessagesMeta>, subcampaignId: number): MessengerMessagesMeta {
  return isNil(meta[subcampaignId]) ? { organizations: {}, users: {} } : meta[subcampaignId];
}

export function fetchNewMessages(subcampaign: Subcampaign, type: OrganizationProfileType): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const {
        pagination,
        metaBySubcampaign,
        isSendingMessage,
        newMessagesMap
      } = getState().messages;

      if (isSendingMessage || isNil(pagination.oldestMessage)) {
        return;
      }

      const url = `${MESSENGER.getConversationMessages(subcampaign.conversationExternalId)}?after=${pagination.oldestMessage}`;
      const response = await get(url);
      const { data } = response;
      const messages = data.messages;

      dispatch(updateLastSeenMessage(response.data.lastSeenMessage, subcampaign.id));

      if (messages.length > 0) {
        const deletedMessages = messages.filter(m => m.isDeleted);

        if (deletedMessages.length > 0) {
          dispatch(markMessageAsDeleted(deletedMessages, subcampaign.id));
        }

        const filteredMessages = filterExistingMessagesByMap(messages, newMessagesMap);

        if (filteredMessages.length === 0) {
          return;
        }

        const newMeta = compareAndMergeMeta(getMetaBySubcampaignId(metaBySubcampaign, subcampaign.id), data.meta);
        const updatedMessagesMap = generateMessagesMap(filteredMessages, newMessagesMap);

        dispatch(saveNewMessagesMap(updatedMessagesMap));
        dispatch(receiveNewMessages(filteredMessages, newMeta, subcampaign.id));
        dispatch(setConversationLastMessage(subcampaign.id, last(messages)));

        if (filteredMessages.find(message =>
          message.metadata.type === 'INFLUENCER_QUOTE'
            || message.metadata.type === 'OFFER_ACCEPT'
            || message.metadata.type === 'OFFER_DECLINE'
            || message.metadata.type === 'SUBCAMPAIGN_CANCEL'
            || message.metadata.type === 'NEGOTIATION_REOPEN'
            || message.metadata.type === 'SET_SHIPPING_ADDRESS'
            || message.metadata.type === 'BRIEF_UPDATE'
            || message.metadata.type === 'INFLUENCER_POST'
        )) {
          dispatch(fetchSubcampaign(subcampaign.id, { showLoading: false }));
          if (type === ORGANIZATION_PROFILE_TYPE_BRAND) {
            dispatch(fetchCampaignOverview(subcampaign.campaign.id));
          }
        }
      }
    } catch (e) {
      logError(e);
    }
  };
}

export function fetchPreviousMessages(subcampaign: Subcampaign): ThunkAction {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoadingMore(true));

      const { metaBySubcampaign, pagination } = getState().messages;

      const page = pagination.page + 1;
      const url = `${MESSENGER.getConversationMessages(subcampaign.conversationExternalId)}?page=${page}&items=${ITEMS}&before=${pagination.oldestMessage}`;

      const response: { data: MessengerConversationMessagesResponse } = await get(url);
      const { data } = response;
      const messages = data.messages;

      dispatch(updateLastSeenMessage(response.data.lastSeenMessage, subcampaign.id));

      if (messages.length === 0) {
        dispatch(setLoadingMore(false));
      } else {
        const newMeta = compareAndMergeMeta(getMetaBySubcampaignId(metaBySubcampaign, subcampaign.id), data.meta);

        dispatch(setPagination(page, first(messages)?.externalId, pagination.oldestMessage));
        dispatch(receivePreviousMessages(messages, newMeta, subcampaign.id));
      }
    } catch (e) {
      dispatch(setLoadingMore(false));
      logError(e);
    }
  };
}

export function fetchMessages(subcampaign: Subcampaign): ThunkAction {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true, subcampaign.id));

      const page = 1;
      const url = `${MESSENGER.getConversationMessages(subcampaign.conversationExternalId)}?page=${page}&items=${ITEMS}`;

      const response: { data: MessengerConversationMessagesResponse } = await get(url);
      const externalId = response.data.conversation.externalId;

      dispatch(updateLastSeenMessage(response.data.lastSeenMessage, subcampaign.id));

      if (externalId) {
        dispatch(markConversationAsRead(externalId));
      }

      const { data } = response;
      const messages = data.messages;

      if (messages.length === 0) {
        dispatch(setMeta(data.meta, subcampaign.id));
        dispatch(setLoading(false, subcampaign.id));
      } else {
        dispatch(setPagination(page, first(messages)?.externalId, last(messages)?.externalId));
        dispatch(receiveMessages(messages, data.meta, subcampaign.id, data.conversation.externalId));
        dispatch(setConversationLastMessage(subcampaign.id, last(messages)));
      }
    } catch (e) {
      dispatch(setLoading(false, subcampaign.id));
      logError(e);
    }
  };
}

export function sendMessage(subcampaign: Subcampaign, message: MessengerSendMessage): ThunkAction {
  const url = MESSENGER.sendMessage(subcampaign.conversationExternalId);

  return async (dispatch, getState) => {
    try {
      const { metaBySubcampaign, newMessagesMap } = getState().messages;

      dispatch(setSendingMessageStatus(true));

      const response = await post(url, { data: message });
      const newMessage = response.data;

      const customMeta = getCustomMetadataByState(getState());
      const newMeta = compareAndMergeMeta(getMetaBySubcampaignId(metaBySubcampaign, subcampaign.id), customMeta);

      const updatedMessagesMap = generateMessagesMap([newMessage], newMessagesMap);

      dispatch(saveNewMessagesMap(updatedMessagesMap));
      dispatch(receiveNewMessage(newMessage, newMeta, subcampaign.id));
      dispatch(setConversationLastMessage(subcampaign.id, newMessage));
      dispatch(setSendingMessageStatus(false));
    } catch (e) {
      dispatch(setSendingMessageStatus(false));
      logError(e);
      throw e; // don't suppress
    }
  };
}

export function sendBulkMessage(body: MessengerBulkMessage) {
  const url = MESSENGER.sendBulkMessage();

  return async (dispatch) => {
    try {
      dispatch(showLoading(url));

      await post(url, { data: body });
    } catch (e) {
      logError(e);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function sendBulkAttachment(body: MessengerBulkAttachment) {
  const url = MESSENGER.sendBulkAttachment();

  return async (dispatch, getState) => {
    try {
      const { runtimeConfig } = getState();

      if (
        !isNil(runtimeConfig.config.uploadAtttachmentMaxFileSize)
        && body.attachment.size > runtimeConfig.config.uploadAtttachmentMaxFileSize
      ) {
        dispatch(danger(t('messenger_attachment_max_size_exceeded', {
          maxSize: getFileSizeInUnits(runtimeConfig.config.uploadAtttachmentMaxFileSize, 'mb')
        })));

        return;
      }

      dispatch(showLoading(url));

      const attachment = await uploadFile(body.attachment);
      const data: { metadata: string, attachment?: object, text?: string } = {
        metadata: ''
      };

      if (attachment === null) {
        dispatch(danger(t('messenger_attachment_upload_error')));
        return;
      }

      if (attachment.type === 'success' && attachment.data) {
        data.attachment = attachment.data;
        data.metadata = JSON.stringify(body.metadata);
        data.text = body.attachment.name;
      }

      if (attachment.type === 'error') {
        throw new UserException(
          attachment.error,
          attachment.file
        );
      }

      await post(
        `${url}?conversation_ids=${body.conversationIds.join(',')}`,
        { data, headers: { 'Collab-Api-Version': 2 } }
      );
    } catch (e) {
      logError(e);
      dispatch(danger(t('messenger_attachment_upload_error')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function sendAttachment(subcampaign: Subcampaign, message: MessengerSendMessage): ThunkAction {
  const url = MESSENGER.sendAttachmentMessage(subcampaign.conversationExternalId);

  return async (dispatch, getState) => {
    const state = getState();
    const tempAttachment = getTemporalUploadingMessage(message, state);
    const { runtimeConfig } = state;

    try {
      if (
        !isNil(runtimeConfig.config.uploadAtttachmentMaxFileSize)
        && (message.file?.size ?? 0) > runtimeConfig.config.uploadAtttachmentMaxFileSize
      ) {
        dispatch(danger(t('messenger_attachment_max_size_exceeded', {
          maxSize: getFileSizeInUnits(runtimeConfig.config.uploadAtttachmentMaxFileSize, 'mb')
        })));

        return;
      }

      const data: { metadata: string, attachment?: object, text?: string } = {
        metadata: ''
      };
      const { metaBySubcampaign, newMessagesMap } = getState().messages;

      const customMeta = getCustomMetadataByState(getState());
      const newMeta = compareAndMergeMeta(getMetaBySubcampaignId(metaBySubcampaign, subcampaign.id), customMeta);

      dispatch(receiveNewMessage(tempAttachment, newMeta, subcampaign.id));

      if (message.file) {
        const attachment = await uploadFile(message.file);

        if (attachment.type === 'success' && attachment.data) {
          data.attachment = attachment.data;
          data.text = message.file.name;
        }

        if (attachment.type === 'error') {
          throw new UserException(
            attachment.error,
            attachment.file
          );
        }
      }

      data.metadata = JSON.stringify(message.metadata);

      const response = await post(url, { data, headers: { 'Collab-Api-Version': 2 } });
      const newMessage = response.data;

      dispatch(setAttachmentSuccess(tempAttachment, subcampaign.id));

      const updatedMessagesMap = generateMessagesMap([newMessage], newMessagesMap);

      dispatch(saveNewMessagesMap(updatedMessagesMap));
      dispatch(receiveNewMessage(newMessage, newMeta, subcampaign.id));
      dispatch(setConversationLastMessage(subcampaign.id, newMessage));
    } catch (e) {
      dispatch(setAttachmentFailed(tempAttachment, subcampaign.id));

      logError(e);
    }
  };
}

export function deleteMessage(
  subcampaignId: number,
  conversationExternalId: string,
  message: MessengerMessage
) {
  const url = MESSENGER.deleteMessage(conversationExternalId, message.externalId);

  return async (dispatch) => {
    try {
      dispatch(showLoading(url));
      await destroy(url, {}, { suppressGlobalNotification: true });

      const deletedMessage = {
        ...message,
        isDeleted: true,
        attachments: []
      };

      dispatch(markMessageAsDeleted([deletedMessage], subcampaignId));
    } catch (e) {
      logError(e);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export default createReducer<MessengerReduxState, any>('messages', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { isLoading, subcampaignId }) => ({
    ...state,
    isLoadingBySubcampaign: {
      ...state.isLoadingBySubcampaign,
      [subcampaignId]: isLoading
    }
  }),
  [LOADING_MORE]: (state, { isLoadingMore }) => ({
    ...state,
    isLoadingMore
  }),
  [UPDATE_PAGINATION]: (state, { page, firstMessage, oldestMessage }) => ({
    ...state,
    pagination: {
      page,
      firstMessage,
      oldestMessage
    }
  }),
  [SET_META]: (state, { meta, subcampaignId }) => ({
    ...state,
    metaBySubcampaign: {
      ...state.metaBySubcampaign,
      [subcampaignId]: meta
    }
  }),
  [RECEIVE_PREVIOUS]: (state, { messages, meta, subcampaignId }) => ({
    ...state,
    messagesBySubcampaign: {
      ...state.messagesBySubcampaign,
      [subcampaignId]: groupMessagesByDate(messages, state.messagesBySubcampaign[subcampaignId])
    },
    metaBySubcampaign: {
      ...state.metaBySubcampaign,
      [subcampaignId]: meta
    },
    isLoadingMore: false
  }),
  [RECEIVE_NEW_MESSAGE]: (state, { message, meta, subcampaignId }) => ({
    ...state,
    messagesBySubcampaign: {
      ...state.messagesBySubcampaign,
      [subcampaignId]: groupMessagesByDate([message], state.messagesBySubcampaign[subcampaignId], true)
    },
    metaBySubcampaign: {
      ...state.metaBySubcampaign,
      [subcampaignId]: meta
    }
  }),
  [RECEIVE_NEW]: (state, { messages, meta, subcampaignId }) => ({
    ...state,
    messagesBySubcampaign: {
      ...state.messagesBySubcampaign,
      [subcampaignId]: groupMessagesByDate(messages, state.messagesBySubcampaign[subcampaignId], true)
    },
    metaBySubcampaign: {
      ...state.metaBySubcampaign,
      [subcampaignId]: meta
    }
  }),
  [RECEIVE]: (state, { messages, meta, subcampaignId, conversationId }) => ({
    ...state,
    conversationId,
    messagesBySubcampaign: {
      ...state.messagesBySubcampaign,
      [subcampaignId]: groupMessagesByDate(messages)
    },
    metaBySubcampaign: {
      ...state.metaBySubcampaign,
      [subcampaignId]: meta
    },
    isLoadingBySubcampaign: {
      ...state.isLoadingBySubcampaign,
      [subcampaignId]: false
    },
    newMessagesMap: {}
  }),
  [UPDATE_LAST_SEEN_MESSAGE]: (state, { lastSeenMessage, subcampaignId }) => ({
    ...state,
    lastSeenMessageBySubcampaign: {
      ...state.lastSeenMessageBySubcampaign,
      [subcampaignId]: lastSeenMessage
    }
  }),
  [SENDING_MESSAGE]: (state, { isSending }) => ({
    ...state,
    isSendingMessage: isSending
  }),
  [UPDATE_MESSAGES_MAP]: (state, { newMessagesMap }) => ({
    ...state,
    newMessagesMap
  }),
  [ATTACHMENT_SUCCESS]: (state, { message, subcampaignId }) => {
    const groupingKey = getMessagesGroupingTitle(message);
    const messagesBySubcampaign = state.messagesBySubcampaign[subcampaignId];
    if (!messagesBySubcampaign) {
      // state was probably reset during attachment upload (e.g. switching to different page)
      return state;
    }

    const messagesGroup = messagesBySubcampaign[groupingKey];
    const messages = messagesGroup.messages;
    const index = findIndex(messages, m => m.externalId === message.externalId);

    return {
      ...state,
      messagesBySubcampaign: {
        ...state.messagesBySubcampaign,
        [subcampaignId]: {
          ...state.messagesBySubcampaign[subcampaignId],
          [groupingKey]: {
            ...state.messagesBySubcampaign[subcampaignId][groupingKey],
            messages: [
              ...messages.slice(0, index),
              ...messages.slice(index + 1, messages.length)
            ]
          }
        }
      }
    };
  },
  [ATTACHMENT_FAILED]: (state, { message, subcampaignId }) => {
    const groupingKey = getMessagesGroupingTitle(message);
    const messagesBySubcampaign = state.messagesBySubcampaign[subcampaignId];
    if (!messagesBySubcampaign) {
      // state was probably reset during attachment upload (e.g. switching to different page)
      return state;
    }

    const messagesGroup = messagesBySubcampaign[groupingKey];
    const messages = messagesGroup?.messages || [];
    const index = findIndex(messages, m => m.externalId === message.externalId);

    return {
      ...state,
      messagesBySubcampaign: {
        ...state.messagesBySubcampaign,
        [subcampaignId]: {
          ...state.messagesBySubcampaign[subcampaignId],
          [groupingKey]: {
            ...state.messagesBySubcampaign[subcampaignId][groupingKey],
            messages: [
              ...messages.slice(0, index),
              {
                ...messages[index],
                attachments: [
                  {
                    ...(messages[index] ? messages[index].attachments[0] : null),
                    status: 'failed'
                  }
                ]
              },
              ...messages.slice(index + 1, messages.length)
            ]
          }
        }
      }
    };
  },
  [MARK_MESSAGE_AS_DELETED]: (state, { deletedMessages, subcampaignId }) => {
    const messagesInGroups = {
      ...state.messagesBySubcampaign[subcampaignId]
    };

    const deletedMessagesMap = deletedMessages.reduce((map, message) => {
      map[message.externalId] = message; // eslint-disable-line no-param-reassign
      return map;
    }, {});

    const deletedMessagesGroupKeys = uniq<string>(deletedMessages.map(message => getMessagesGroupingTitle(message)));

    deletedMessagesGroupKeys.forEach((groupKey) => {
      messagesInGroups[groupKey] = {
        ...messagesInGroups[groupKey],
        messages: messagesInGroups[groupKey].messages.map(message =>
          ((deletedMessagesMap[message.externalId] && !message.isDeleted) ? deletedMessagesMap[message.externalId] : message))
      };
    });

    return {
      ...state,
      messagesBySubcampaign: {
        ...state.messagesBySubcampaign,
        [subcampaignId]: messagesInGroups
      }
    };
  }
});
